import React, { useEffect, useState } from 'react';
import { Container } from '../components/common/Container';
import { Button } from '../components/common/Button';
import { Eyebrow, Badge } from '../components/common/Badge';
import { Icon } from '../lib/icons';
import { dataService } from '../services/dataService';
import { useLanguage } from '../context/LanguageContext';

function FilterPills({ filters, active, onChange }) {
  return (
    <div className="no-scrollbar -mx-6 flex gap-3 overflow-x-auto px-6 pb-2">
      {filters.map((f) => {
        const isActive = active === f.id;
        return (
          <button
            key={f.id}
            onClick={() => onChange(f.id)}
            className={`flex h-11 shrink-0 items-center gap-2 rounded-btn border px-4 font-sans text-sm transition-colors ${
              isActive ? 'border-paelbi-brown bg-paelbi-brown text-white' : 'border-paelbi-line bg-white text-paelbi-ink/80 hover:border-paelbi-brown/50'
            }`}
          >
            {f.icon && <Icon name={f.icon} size={18} strokeWidth={1.75} />}
            {f.label}
          </button>
        );
      })}
    </div>
  );
}

function Heart() {
  const [on, setOn] = useState(false);
  return (
    <button onClick={() => setOn((v) => !v)} aria-label="Preferiti" aria-pressed={on}
      className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-paelbi-brown shadow-soft transition-transform hover:scale-105">
      <Icon name="heart" size={18} className={on ? 'fill-paelbi-brown' : ''} />
    </button>
  );
}

function ServiceCard({ card }) {
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&margin=0&color=2B282B&bgcolor=FFFFFF&data=${encodeURIComponent('PAELBI-' + card.id)}`;
  return (
    <article className="group flex flex-col overflow-hidden rounded-card border border-paelbi-line bg-white shadow-card transition-all hover:-translate-y-1 hover:shadow-soft">
      <div className="relative">
        <img src={card.image} alt={card.title} loading="lazy" className="h-44 w-full object-cover" />
        <Heart />
        <span className="absolute bottom-3 left-3"><Badge className="bg-white/90">{card.badge}</Badge></span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-display text-xl text-paelbi-ink">{card.title}</h3>
        <p className="mt-1 font-sans text-sm font-bold text-paelbi-brown">{card.discount}</p>
        <p className="mt-2 flex-1 font-sans text-sm leading-relaxed text-paelbi-ink/70">{card.text}</p>
        <div className="mt-5 flex items-end justify-between border-t border-paelbi-line pt-4">
          <div className="flex flex-col gap-2 font-sans text-xs text-paelbi-ink/70">
            <span className="flex items-center gap-1.5"><Icon name="mapPin" size={15} />{card.meta1}</span>
            <span className="flex items-center gap-1.5"><Icon name="clock" size={15} />{card.meta2}</span>
          </div>
          <img src={qr} alt="QR code convenzione" loading="lazy" className="h-16 w-16 rounded-[6px]" />
        </div>
      </div>
    </article>
  );
}

export function Servizi() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState('all');
  const { t, lang } = useLanguage();
  useEffect(() => { dataService.getServices().then(setData); }, [lang]);
  if (!data) return null;

  const cards = active === 'all' ? data.cards : data.cards.filter((c) => c.cat === active);

  return (
    <div className="pb-20 pt-8">
      {/* HERO */}
      <Container>
        <section className="grid grid-cols-1 items-stretch gap-0 overflow-hidden rounded-card border border-paelbi-line bg-white shadow-card md:grid-cols-2">
          <div className="flex flex-col justify-center p-8 md:p-12">
            <Eyebrow>{data.hero.eyebrow}</Eyebrow>
            <h1 className="mt-4 font-display text-3xl leading-[1.15] text-paelbi-ink md:text-[40px]">
              {data.hero.title.map((l, i) => <span key={`h-${i}`} className="block">{l}</span>)}
            </h1>
            <p className="mt-5 max-w-md font-sans text-base leading-relaxed text-paelbi-ink/70">{data.hero.text}</p>
          </div>
          <div className="relative min-h-[240px] md:min-h-0">
            <img src={data.hero.image} alt="Servizi e convenzioni" className="absolute inset-0 h-full w-full object-cover object-center" />
          </div>
        </section>
      </Container>

      {/* FILTERS */}
      <Container className="mt-8">
        <FilterPills filters={data.filters} active={active} onChange={setActive} />
      </Container>

      {/* CARDS */}
      <Container className="mt-10">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl text-paelbi-ink md:text-[28px]">{t('Servizi e convenzioni per te')}</h2>
          <div className="hidden items-center gap-2 rounded-btn border border-paelbi-line bg-white px-4 py-2 text-sm text-paelbi-ink/70 sm:flex">
            <span>{t('Ordina per')}</span>
            <span className="font-semibold text-paelbi-ink">{t('Consigliati')}</span>
            <Icon name="chevronRight" size={14} className="rotate-90" />
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {cards.map((c) => <ServiceCard key={c.id} card={c} />)}
        </div>
      </Container>

      {/* BANNERS */}
      <Container className="mt-14 space-y-4">
        {data.banners.map((b) => (
          <div key={b.title} className="flex flex-col items-start gap-4 rounded-card border border-paelbi-line bg-white p-6 shadow-card sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-paelbi-bg2">
                <Icon name={b.icon} size={20} className="text-paelbi-brown" />
              </span>
              <div>
                <h3 className="font-display text-lg text-paelbi-ink">{b.title}</h3>
                <p className="mt-1 font-sans text-sm text-paelbi-ink/70">{b.text}</p>
              </div>
            </div>
            <Button variant={b.type === 'primary' ? 'primary' : 'secondary'} iconLeft={b.type === 'outline' ? 'bellRing' : undefined} className="shrink-0">
              {b.cta}
            </Button>
          </div>
        ))}
      </Container>
    </div>
  );
}

export default Servizi;
